import { lazy, useCallback, useEffect } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { useImmer } from 'use-immer';

import { Job, OutletContext, PlatformApp } from '@typings';
import {
  getPlatformAppInstnceParams,
  getPlatformAppsInstance,
} from '@services/platformApps.services';
import { contextNamesSelector } from '@selectors';
import { useSelector } from '@hooks';
import { as, invariant, path, toastifyResponseError } from '@utils';

import { ChaseSpinner } from '@components';
import { Layout, PlatformAppManagementLayout } from '@components/Layouts';

import { withSuspense } from './utils';

const PlatformAppsInstanceOverviewPage = lazy(
  () => import('@pages/PlatformAppsInstance/PlatformAppsInstanceOverviewPage'),
);
const PlatformAppsObservabilityPage = lazy(
  () => import('@pages/PlatformAppsInstance/PlatformAppsObservabilityPage'),
);
const PlatformAppsInstanceLogPage = lazy(
  () => import('@pages/PlatformAppsInstance/PlatformAppsInstanceLogPage'),
);

type State = {
  instance: PlatformApp.Instance | null;
  input: OutletContext.PlatformAppsInstance['input'];
  output: OutletContext.PlatformAppsInstance['output'];
};

export default function PlatformAppManagementRoutes() {
  const { organizationName, clusterName, projectName } =
    useSelector(contextNamesSelector);

  const params = useParams();

  const [{ instance, input, output }, setAppState] = useImmer<State>({
    instance: null,
    input: null,
    output: null,
  });

  const instanceId = params.instanceId!;
  const { name } = as.o<PlatformApp.Instance>(instance);

  const getAppParamsByType = useCallback(
    async (type: 'input' | 'output', restoreRef?: boolean) => {
      try {
        invariant(clusterName);
        invariant(organizationName);
        invariant(projectName);

        const params = await getPlatformAppInstnceParams(
          {
            instanceId,
            type,
            clusterName,
            projectName,
            organizationName,
          },
          restoreRef,
        );

        setAppState((state) => {
          state[type] = params;
        });
      } catch (error) {
        type PlainError = {
          message?: string;
          detail?: { message?: string };
        };
        const responseError = error as PlainError;

        if (responseError?.detail?.message) {
          setAppState((state) => {
            state[type] = responseError.detail?.message as string;
          });
        }
      }
    },
    [instanceId, organizationName, clusterName, projectName, setAppState],
  );

  const fetchInstanceParams = useCallback(() => {
    getAppParamsByType('input', true);
    getAppParamsByType('output');
  }, [getAppParamsByType]);

  const fetchInstance: OutletContext.PlatformAppsInstance['getInstance'] =
    useCallback(async () => {
      try {
        invariant(instanceId);

        const instance = await getPlatformAppsInstance({ instanceId });

        setAppState((state) => {
          state.instance = instance;
        });

        return instance;
      } catch (error) {
        toastifyResponseError(error);

        throw error;
      }
    }, [instanceId, setAppState]);

  useEffect(() => {
    const getInstance = async () => {
      try {
        await fetchInstance();
      } catch (error) {
        toastifyResponseError(error);
      }
    };

    getInstance();
    fetchInstanceParams();

    const intervalId = setInterval(() => {
      getInstance();
      fetchInstanceParams();
    }, 10000);

    return () => {
      clearInterval(intervalId);
    };
  }, [fetchInstanceParams, fetchInstance]);

  if (!instance) {
    return (
      <Layout title={name}>
        <Layout.Content className="relative flex justify-center">
          <ChaseSpinner color="black" className="absolute mt-40 h-14 w-14" />
        </Layout.Content>
      </Layout>
    );
  }

  return (
    <Routes>
      <Route
        element={
          <PlatformAppManagementLayout
            input={input}
            output={output}
            instance={instance}
            getInstance={fetchInstance}
            getInstanceParams={fetchInstanceParams}
          />
        }
      >
        <Route index element={withSuspense(PlatformAppsInstanceOverviewPage)} />
        <Route
          path="details"
          element={withSuspense(PlatformAppsInstanceOverviewPage)}
        />
        <Route
          path="observability"
          element={withSuspense(PlatformAppsObservabilityPage)}
        />
        <Route path="log" element={withSuspense(PlatformAppsInstanceLogPage)} />
        <Route
          path="*"
          element={
            <Navigate
              replace
              to={path.appsInstance(instanceId, Job.Tab.Overview)}
            />
          }
        />
      </Route>
    </Routes>
  );
}
